import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../Button/Button.js';
import { Icon } from '../Icon/Icon.js';
import { Input } from '../Input/Input.js';
import './MoveModal.css';

export const MoveModal = ({
  isOpen = false,
  onClose,
  onMove,
  folders = [],
  currentFolderId = null,
  itemType = 'color', // 'color' or 'folder'
  itemName = '',
  className = '',
  ...props
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredFolders, setFilteredFolders] = useState(folders);
  const modalRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter folders based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredFolders(folders);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = folders.filter(folder => {
        // Search in folder name
        if (folder.name.toLowerCase().includes(query)) {
          return true;
        }
        // Search in folder path if available
        if (folder.folderPath && folder.folderPath.toLowerCase().includes(query)) {
          return true;
        }
        return false;
      });
      setFilteredFolders(filtered);
    }
  }, [folders, searchQuery]);

  // Focus search input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen, onClose]);

  const handleMoveToFolder = (folderId) => {
    onMove(folderId);
    onClose();
  };

  const handleMoveToRoot = () => {
    onMove(null); // null means move to root (no folder)
    onClose();
  };

  if (!isOpen) return null;

  const baseClass = 'move-modal';
  const combinedClassName = [baseClass, className].filter(Boolean).join(' ');

  return createPortal(
    <div className="move-modal__overlay">
      <div className="scrim" onClick={onClose} />
      <div ref={modalRef} className={combinedClassName}>
        <div className="move-modal__header">
          <h3 className="move-modal__title">
            Move {itemType === 'folder' ? 'Folder' : 'Color'}
          </h3>
          <Button
            type="ghost"
            size="small"
            icon={<Icon name="close" size="small" />}
            onClick={onClose}
            className="move-modal__close-button"
          />
        </div>
        
        <div className="move-modal__content">
          <p className="move-modal__description">
            Move "{itemName}" to:
          </p>
          
          <div className="move-modal__search">
            <Input
              ref={searchInputRef}
              type="text"
              style="outline"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search folders..."
              className="move-modal__search-input"
            />
          </div>
          
          <div className="move-modal__options">
            <button
              className="move-modal__option"
              onClick={handleMoveToRoot}
            >
              <div className="move-modal__option-content">
                <Icon name="folder" size="medium" />
                <span>Root (No folder)</span>
              </div>
            </button>
            
            {filteredFolders.map((folder) => (
              <button
                key={folder.id}
                className="move-modal__option"
                onClick={() => handleMoveToFolder(folder.id)}
                disabled={folder.id === currentFolderId}
              >
                <div className="move-modal__option-content">
                  <Icon name="folder" size="medium" />
                  <div className="move-modal__option-text">
                    <span className="move-modal__option-name">{folder.folderPath || folder.name}</span>
                  </div>
                </div>
              </button>
            ))}
            
            {filteredFolders.length === 0 && searchQuery.trim() && (
              <div className="move-modal__no-results">
                No folders found matching "{searchQuery}"
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
